// Curva semanal (área + línea) para evolución de ROM, dolor, etc.
// Compartida entre ProgresoPaciente (vista paciente) y DetallePaciente
// (vista kine). Colores desde la paleta --chart-* del tema.

import { useId } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { PuntoSemana } from "@/lib/paciente/useEvolucionPaciente";

export function EvolucionChart({
  titulo,
  subtitulo,
  data,
  unidad,
  color = "var(--chart-2)",
  dominio,
  destacado,
  vacio = "Todavía no hay mediciones",
}: {
  titulo: string;
  subtitulo: string;
  data: PuntoSemana[];
  /** Sufijo de los valores ("°", "/10"). */
  unidad: string;
  color?: string;
  dominio?: [number, number];
  /** Cifra grande arriba a la derecha (ej. "+18°"). */
  destacado?: { valor: string; label: string; tono?: "bueno" | "neutro" };
  vacio?: string;
}) {
  // useId trae ":" que rompe url(#...) en algunos navegadores.
  const gradId = `evo${useId().replace(/:/g, "")}`;

  return (
    <div className="bg-card rounded-3xl p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h2 className="text-lg">{titulo}</h2>
          <p className="text-sm text-muted-foreground truncate">{subtitulo}</p>
        </div>
        {destacado && data.length > 0 && (
          <div className="text-right shrink-0">
            <p
              className={`text-2xl font-[800] tabular-nums leading-none ${
                destacado.tono === "bueno" ? "text-green-600 dark:text-success" : "text-primary"
              }`}
            >
              {destacado.valor}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">{destacado.label}</p>
          </div>
        )}
      </div>

      {data.length === 0 ? (
        <div className="flex items-center justify-center min-h-[180px] bg-muted/30 rounded-xl p-6">
          <p className="text-sm text-muted-foreground text-center">{vacio}</p>
        </div>
      ) : (
        <div className="h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="var(--border)" />
              <XAxis
                dataKey="week"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
              />
              <YAxis
                domain={dominio ?? ["dataMin - 5", "dataMax + 5"]}
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
              />
              <Tooltip
                formatter={(v: number) => [`${v}${unidad}`, titulo]}
                contentStyle={{
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  fontSize: 12,
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2.5}
                fill={`url(#${gradId})`}
                dot={{ r: 3, fill: color, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
